import React, { useEffect } from "react";
import toastr from "toastr";
import {
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  textField: {
    width: "184px",
  },
  permissionsChipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginTop: "8px",
    marginBottom: "4px",
    padding: "8px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    minHeight: "40px",
    backgroundColor: "#f9f9f9",
  },
  noPermissionsText: {
    color: "#999",
    fontStyle: "italic",
    fontSize: "13px",
  },
  chipLabel: {
    fontSize: "13px",
    color: "#555",
    marginBottom: "2px",
    marginTop: "10px",
  },
  permissionHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "10px",
    marginBottom: "4px",
  },
  permissionCountText: {
    fontSize: "13px",
    color: "#555",
    fontWeight: 500,
  },
  permissionActionButtons: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  selectAllBtn: {
    minWidth: "auto",
    padding: "4px 12px",
    fontSize: "12px",
    textTransform: "none",
    borderRadius: "4px",
    lineHeight: "1.5",
    backgroundColor: "#1976d2",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1565c0",
    },
    "&:disabled": {
      backgroundColor: "#e0e0e0",
      color: "#9e9e9e",
    },
  },
  deselectAllBtn: {
    minWidth: "auto",
    padding: "4px 12px",
    fontSize: "12px",
    textTransform: "none",
    borderRadius: "4px",
    lineHeight: "1.5",
    backgroundColor: "#d32f2f",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#c62828",
    },
    "&:disabled": {
      backgroundColor: "#e0e0e0",
      color: "#9e9e9e",
    },
  },
  dividerLine: {
    width: "1px",
    height: "18px",
    backgroundColor: "#ccc",
  },
}));

export const PERMISSIONS_BY_TYPE = {
  "Back Office Permissions": [
    "Holiday Master",
    "Issuer Master",
    "Rating Organization Master",
    "Security Type",
    "Security Master",
    "Quotes",
    "Wishlist",
    "Users",
    "Debarred Users",
    "Partner Users",
    "Orders",
    "Create Order",
    "Check RFQ Sell Order",
    "Create RFQ Sell Order",
    "IPO",
    "Create IPO Orders",
    "Mutual Funds",
    "MF Investor Profile",
  ],
  "Business Development Permissions": [
    "MF Portfolio Planner",
    "Form 121",
    "Users",
    "Order",
  ],
  "Research": ["CKYC Module", "Issuer Master"],
  "Compliance": ["CKYC Module"],
};

const PERMISSION_TYPES = Object.keys(PERMISSIONS_BY_TYPE);

const AddAdminUserModal = (props) => {
  const classes = useStyles();
  const [currentState, setCurrentState] = React.useState(props.initialState);
  const [currentStateEdit, setCurrentStateEdit] = React.useState(
    props.toBeEdited
  );
  const [permissionType, setPermissionType] = React.useState("");
  const [permissions, setPermissions] = React.useState([]);

  const resetStateAndClose = (event) => {
    event.preventDefault();
    setCurrentState(props.initialState);
    setPermissionType("");
    setPermissions([]);
    props.onClose(event);
  };

  const getPermissionMappingForUser = (userUUID) => {
    if (!props.permissionMappings || !userUUID) return null;
    return props.permissionMappings.find(
      (entry) => entry.adminUser?.uuid === userUUID
    );
  };

  const getExistingPermissionsForUser = (userUUID) => {
    const mapping = getPermissionMappingForUser(userUUID);
    return mapping ? mapping.permissions : [];
  };

  useEffect(() => {
    if (props.editMode) {
      setCurrentStateEdit(props.toBeEdited);

      const userUUID = props.toBeEdited?.uuid;
      const mapping = getPermissionMappingForUser(userUUID);
      const existingType =
        props.toBeEdited?.permission_type ||
        mapping?.adminUser?.permission_type ||
        "";

      setPermissionType(existingType);

      const actualPermissions = mapping ? mapping.permissions : [];

      if (existingType && actualPermissions.length > 0) {
        const typePermissions = PERMISSIONS_BY_TYPE[existingType] || [];
        const validPermissions = actualPermissions.filter((p) =>
          typePermissions.includes(p)
        );
        const finalPermissions =
          validPermissions.length > 0
            ? validPermissions
            : actualPermissions;
        setPermissions(finalPermissions);
      } else if (existingType) {
        setPermissions(PERMISSIONS_BY_TYPE[existingType] || []);
      } else {
        setPermissions([]);
      }
    } else {
      setPermissionType("");
      setPermissions([]);
      setCurrentState(props.initialState);
    }
  }, [props.editMode, props.toBeEdited, props.permissionMappings]);

  const getFailureCond = () => {
    if (props.editMode) {
      return (
        currentStateEdit.mobileNumber === "" ||
        currentStateEdit.emailId === "" ||
        currentStateEdit.firstName === "" ||
        currentStateEdit.lastName === ""
      );
    } else {
      return (
        currentState.firstName === "" ||
        currentState.lastName === "" ||
        currentState.mobileNumber === "" ||
        currentState.emailId === "" ||
        currentState.password === ""
      );
    }
  };

  const updateCanSeeAll = (value) => {
    if (props.editMode) {
      setCurrentStateEdit((previousValues) => ({
        ...previousValues,
        can_see_all: value,
      }));
    } else {
      setCurrentState((previousValues) => ({
        ...previousValues,
        can_see_all: value,
      }));
    }
  };

  const handlePermissionTypeChange = (event) => {
    const type = event.target.value;
    setPermissionType(type);

    if (type) {
      setPermissions(PERMISSIONS_BY_TYPE[type] || []);
    } else {
      setPermissions([]);
    }

    if (type === "Back Office Permissions") {
      updateCanSeeAll(true);
    }
  };

  const handlePermissionChipToggle = (permission) => {
    setPermissions((prev) => {
      if (prev.includes(permission)) {
        return prev.filter((p) => p !== permission);
      } else {
        return [...prev, permission];
      }
    });
  };

  const handleSelectAllPermissions = () => {
    const allForType = PERMISSIONS_BY_TYPE[permissionType] || [];
    setPermissions(allForType);
  };

  const handleDeselectAllPermissions = () => {
    setPermissions([]);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (getFailureCond()) {
      toastr.error(
        "One or more fields are empty. Please fill all the fields to proceed!"
      );
    } else if (props.editMode) {
      let state = { ...currentStateEdit, permissionType, permissions };
      setCurrentStateEdit(props.initialState);
      setPermissionType("");
      setPermissions([]);
      props.onUpdate(event, state);
    } else {
      let state = { ...currentState, permissionType, permissions };
      setCurrentState(props.initialState);
      setPermissionType("");
      setPermissions([]);
      props.onSubmit(event, state);
    }
  };

  const handleChange = (event) => {
    event.persist();
    if (props.editMode) {
      setCurrentStateEdit((previousValues) => ({
        ...previousValues,
        [event.target.name]: event.target.value,
      }));
    } else {
      setCurrentState((previousValues) => ({
        ...previousValues,
        [event.target.name]: event.target.value,
      }));
    }
  };

  const allTypePermissions = PERMISSIONS_BY_TYPE[permissionType] || [];
  const allSelected =
    allTypePermissions.length > 0 &&
    allTypePermissions.every((p) => permissions.includes(p));
  const noneSelected = permissions.length === 0;

  return (
    <Dialog
      open={props.show}
      onClose={props.onClose}
      className={classes.modal}
    >
      <DialogTitle>
        {props.editMode ? "Edit Admin User" : "Create Admin User"}
      </DialogTitle>
      <DialogContent style={{ width: "500px" }}>
        <DialogContentText component="div">
          <TextField
            type="text"
            name="firstName"
            variant="outlined"
            fullWidth
            label="First Name"
            defaultValue={
              props.editMode
                ? currentStateEdit.firstName
                : currentState.firstName
            }
            onChange={handleChange}
          />

          <Grid item xs={12} style={{ marginTop: "10px" }}>
            <TextField
              type="text"
              name="lastName"
              variant="outlined"
              fullWidth
              label="Last Name"
              defaultValue={
                props.editMode
                  ? currentStateEdit.lastName
                  : currentState.lastName
              }
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: "10px" }}>
            <TextField
              type="text"
              name="mobileNumber"
              variant="outlined"
              fullWidth
              label="Mobile Number"
              disabled={props.editMode}
              defaultValue={
                props.editMode
                  ? currentStateEdit.mobileNumber
                  : currentState.mobileNumber
              }
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: "10px" }}>
            <TextField
              type="text"
              name="emailId"
              variant="outlined"
              fullWidth
              label="Email ID"
              defaultValue={
                props.editMode
                  ? currentStateEdit.emailId
                  : currentState.emailId
              }
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: "10px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    props.editMode
                      ? !!currentStateEdit.can_see_all
                      : !!currentState.can_see_all
                  }
                  onChange={(event) => updateCanSeeAll(event.target.checked)}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label="Can See All"
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: "10px" }}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="permission-type-label">
                Permissions Type
              </InputLabel>
              <Select
                labelId="permission-type-label"
                label="Permissions Type"
                value={permissionType}
                onChange={handlePermissionTypeChange}
              >
                {PERMISSION_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {permissionType && (
            <Grid item xs={12}>
              <div className={classes.permissionHeaderRow}>
                <span className={classes.permissionCountText}>
                  Permissions ({permissions.length} / {allTypePermissions.length})
                </span>
                <div className={classes.permissionActionButtons}>
                  <Button
                    size="small"
                    variant="contained"
                    className={classes.selectAllBtn}
                    onClick={handleSelectAllPermissions}
                    disabled={allSelected}
                  >
                    Select All
                  </Button>
                  <div className={classes.dividerLine} />
                  <Button
                    size="small"
                    variant="contained"
                    className={classes.deselectAllBtn}
                    onClick={handleDeselectAllPermissions}
                    disabled={noneSelected}
                  >
                    Deselect All
                  </Button>
                </div>
              </div>

              <div className={classes.permissionsChipContainer}>
                {allTypePermissions.length > 0 ? (
                  allTypePermissions.map((permission) => {
                    const isSelected = permissions.includes(permission);
                    return (
                      <Chip
                        key={permission}
                        label={permission}
                        onClick={() => handlePermissionChipToggle(permission)}
                        color={isSelected ? "primary" : "default"}
                        variant={isSelected ? "default" : "outlined"}
                        style={{
                          cursor: "pointer",
                          marginBottom: "2px",
                        }}
                      />
                    );
                  })
                ) : (
                  <span className={classes.noPermissionsText}>
                    No permissions available for this type
                  </span>
                )}
              </div>
            </Grid>
          )}

          {!permissionType && (
            <Grid item xs={12}>
              <div
                style={{
                  marginTop: "8px",
                  color: "#999",
                  fontStyle: "italic",
                  fontSize: "13px",
                }}
              >
                Select a Permissions Type to see available permissions
              </div>
            </Grid>
          )}

          {props.editMode ? (
            ""
          ) : (
            <Grid item xs={12} style={{ marginTop: "10px" }}>
              <TextField
                type="text"
                name="password"
                variant="outlined"
                fullWidth
                label="Password"
                defaultValue={currentState.password}
                onChange={handleChange}
              />
            </Grid>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetStateAndClose}>Cancel</Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAdminUserModal;